import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.offWhite};
  padding-top: 200px;
  
  @media (max-width: 768px) {
    padding-top: 160px;
  }
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 0.875rem;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  border: 1px solid ${props => props.theme.colors.greyBackground};
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: #666;
  margin-bottom: 2.5rem;
  font-size: 1.1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.1);
  }
  
  &.error {
    border-color: #F44336;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 1.2rem 2rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #F44336;
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const HelpText = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-top: 1rem;
  text-align: center;
  line-height: 1.6;
`;

const FarmShopOrderLookup = () => {
  const navigate = useNavigate();
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getApiUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3000/api/farm-order-lookup.php';
    } else if (hostname.includes('staging')) {
      return 'https://staging.belvoircastle.com/api/farm-order-lookup.php';
    } else {
      return 'https://www.belvoircastle.com/api/farm-order-lookup.php';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!orderNumber.trim()) {
      setError('Please enter your order number');
      setLoading(false);
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber.trim(),
          email: email.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Order not found. Please check your details and try again.');
      }

      // Navigate to order details page with order data
      navigate('/farm-shop/order/view', {
        state: { order: data.order },
      });
    } catch (err) {
      setError(err.message || 'Unable to find your order. Please check your order number and email address.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <ContentContainer>
        <Card>
          <Title>View Your Order</Title>
          <Subtitle>Enter your order details to view your receipt and order status</Subtitle>

          {error && (
            <ErrorMessage>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{error}</span>
            </ErrorMessage>
          )}

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="order_number">Order Number *</Label>
              <Input
                id="order_number"
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g., 12345"
                required
                disabled={loading}
                className={error && !orderNumber ? 'error' : ''}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                disabled={loading}
                className={error && !email ? 'error' : ''}
              />
            </FormGroup>

            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Searching...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSearch} />
                  View Order
                </>
              )}
            </Button>
          </Form>

          <HelpText>
            <strong>Need help?</strong> If you can't find your order, please check your confirmation email 
            for your order number, or contact us at{' '}
            <a href="mailto:info@belvoircastle.com" style={{ color: '#9d6d49', textDecoration: 'underline' }}>
              info@belvoircastle.com
            </a>
          </HelpText>
        </Card>
      </ContentContainer>
      <Footer />
    </Container>
  );
};

export default FarmShopOrderLookup;


